/**
 * lib/help-fab-labels.ts — the help button's one label, and where it must not appear.
 * 2026-09-22; one label on every route since 2026-09-23.
 *
 * ═══ WHY THIS FILE EXISTS ═══
 * Lars, 2026-09-22: "we have a brand kit, and when we change something… if we
 * change the chatbot in one page, it should automatically, because it's the same
 * chat box, give the same information everywhere."
 *
 * HelpFab mounts ONCE, in app/layout.tsx, next to <ConsentManager />. It reads
 * usePathname() only to decide whether it appears at all (the suppression list
 * below). A new page gets the button automatically and needs no line of its own.
 *
 * ═══ ONE LABEL, EVERY ROUTE (2026-09-23) ═══
 * The per-route wording ("Questions before joining? Ask" and nine others) is
 * gone. On phones those pills were wide, got cut off ("Questions before jo…")
 * and covered page content. Lars, 2026-09-23: desktop shows the icon + "Ask" on
 * every page; phones show the icon alone, as a round dot (globals.css §C50).
 * Brand kit v2.56 §14.5 (Aria).
 *
 * The word "AI" never appears on or in the button — the lightning-bolt icon
 * carries "instant answer" instead. Do not add a per-route map back: one
 * doorway, one word.
 */

/**
 * The label every route gets. Pinned 2026-09-23 (Lars, via Eleanor).
 * Wording is Iris's and Hazel's lane, never the engineer's.
 */
export const DEFAULT_HELP_FAB_LABEL = 'Ask';

/**
 * Routes the pill must NOT appear on. Pinned by Eleanor 2026-09-22.
 *
 * Matched as a path prefix, so an entry here also covers everything beneath
 * it.
 *
 * `/checkout` — a floating overlay pinned to the bottom of a payment form can
 * cover a field mid-payment. Nothing floats over a page where someone is
 * entering card details. SOP-WEBSITE-03 rule 19 governs that surface.
 *
 * `/join` is deliberately NOT here: it is a sales page, not a payment form, and
 * a visitor deciding whether to join is exactly who the pill is for.
 *
 * Adding an entry here is a product decision, not an engineering one — route it
 * to Eleanor before you add a line.
 */
export const HELP_FAB_SUPPRESSED_ROUTES: readonly string[] = Object.freeze([
  '/checkout',
]);

/** Strip a trailing slash, but never turn the site root into an empty string. */
function normalisePath(pathname: string): string {
  if (!pathname) return '/';
  const trimmed = pathname.replace(/\/+$/, '');
  return trimmed === '' ? '/' : trimmed;
}

/**
 * Does `pattern` cover `pathname`? Exact match, or `pathname` sits beneath
 * `pattern` as a path segment. `/` covers only itself.
 */
function covers(pattern: string, pathname: string): boolean {
  if (pattern === pathname) return true;
  if (pattern === '/') return false;
  return pathname.startsWith(pattern + '/');
}

/** True when the pill must not render on this route. */
export function isHelpFabSuppressed(pathname: string | null | undefined): boolean {
  const path = normalisePath(pathname ?? '/');
  return HELP_FAB_SUPPRESSED_ROUTES.some((p) => covers(p, path));
}
